'use client';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import Menu, { type MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha, styled } from '@mui/material/styles';
import * as React from 'react';

import { Button } from '@campaign-manager/react/components';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 3,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: 'rgb(55, 65, 81)',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300],
    }),
  },
}));

interface DropdownMenuItem {
  label: string;
  handleClick?: () => void;
  disabled?: boolean;
}

interface Props {
  variant: MuiButtonProps['variant'];
  color: MuiButtonProps['color'];
  label: string;
  items: DropdownMenuItem[];
  disabled?: boolean;
  loading?: boolean;
}

export default function DropdownMenu({ variant, color, label, items, disabled, loading }: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        variant={variant}
        color={color}
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        disabled={disabled}
        loading={loading}
        sx={{ px: 2 }}
      >
        {!loading ? label : ''}
      </Button>
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {items.map(({ label, handleClick, disabled: itemDisabled }) => (
          <MenuItem
            key={label}
            onClick={() => {
              if (!itemDisabled) {
                handleClick?.();
                handleClose();
              }
            }}
            disabled={itemDisabled}
            disableRipple
          >
            {label}
          </MenuItem>
        ))}
      </StyledMenu>
    </div>
  );
}
